import React, { useRef, useState, useEffect } from 'react';
import styles from './DataDashboard.module.scss';
import DashboardCard from './components/DashboardCard/DashboardCard';
import { navigate } from '@reach/router';
import * as Icon from 'react-feather';
import { Router } from '@reach/router';

import LowUsageSchools from './components/LowUsageSchools/LowUsageSchools';
import HighUsageSchools from './components/HighUsageSchools/HighUsageSchools';
import LowTakeupSchools from './components/LowTakeupSchools/LowTakeupSchools';
import SchoolSearch from './components/SchoolSearch/SchoolSearch';
import SchoolStats from './components/SchoolStats/SchoolStats';

import SchoolHome from '../School/scenes/SchoolHome';

import { ADMIN_STATS, ACADEMIC_MONTHLY_REPORTS } from './services/graphql';
import { monthNames, radiusOpts, lineColors, academicMonthOrder, calendarMonthOrder } from './utils/chart-helpers';

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from 'moment';

import Tabs from '../../components/Tabs';
import Tab from '../../components/Tab';
import { useLazyQuery } from '@apollo/react-hooks';
import { useEffectOnce } from 'react-use';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
);

const SELECTED_YEAR_TYPE = {
    CALENDARISTIC: 'calendaristic',
    ACADEMIC: 'academic'
}

const DataDashboard = () => {
    return (
        <>
            <Router>
                <DataDashboardContent
                    path='/'
                    default
                />
                <LowUsageSchools path='/lowUsageSchools' />
                <HighUsageSchools path='/highUsageSchools' />
                <LowTakeupSchools path='/lowTakeupSchools' />
                <SchoolSearch path='/schoolSearch' />
                <SchoolStats path='/schoolStats/:schoolId' />
                <SchoolHome
                    path='/schoolStats/schoolHome/:schoolId'
                    newDashboard={true}
                />
            </Router>
        </>
    );
};

const DataDashboardContent = () => {
    const [selectedData, setSelectedData] = useState([]);
    const [readingLogs, setReadingLogs] = useState(null);
    const [booksCompleted, setBooksCompleted] = useState(null);
    const [bookTypes, setBookTypes] = useState(null);
    const [loggedBy, setLoggedBy] = useState(null);
    const [adminStatsData, setAdminStatsData] = useState(null);
    const [toolsData, setToolsData] = useState(null);
    const [selectedYear, setSelectedYear] = useState(moment().year());
    const [selectedYearAdminStats, setSelectedYearAdminStats] = useState(moment().toDate().getMonth() >= 8 ? moment().year() + 1 : moment().year());

    const [selectedYearType, setSelectedYearType] = useState(SELECTED_YEAR_TYPE.CALENDARISTIC);

    const adminStatsMap = {
        activeParentsCount: 'Active Parents',
        activeSchoolsCount: 'Active Schools',
        activeStudentsCount: 'Active Students',
        activeTeachersCount: 'Active Teachers',
        cancelledSchoolsCount: 'Cancelled Schools',
        nonWondeSchoolCount: 'Manual Schools',
        wondeSchoolsCount: 'Wonde Schools',
        logs: 'Total Reading Logs',
        lowUsageSchools: 'Low Usage Schools',
        highUsageSchools: 'High Usage Schools',
        lowTakeupSchools: 'Low Takeup Schools'
    };

    let labels = [];

    const readingLogsChartRef = useRef(null);
    const booksCompletedChartRef = useRef(null);

    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: false,
            },
        },
        scales: {
            y: {
                beginAtZero: true,
            },
            x: {
                offset: selectedData.length === 0 ? true : false,
            },
        },
    };

    const [getAdminStats, { error: adminStatsError, loading: adminStatsLoading }] = useLazyQuery(ADMIN_STATS, {
        fetchPolicy: 'network-only',
        variables: {
            academicYear: selectedYearAdminStats
        },
        onCompleted: (data) => {
            if (data.adminStats) {
                const appStats = { ...data.adminStats.filter((adminStat) => adminStat.id === 'app_stats')[0].appStats };
                const appLogs = data.adminStats.filter((adminStat) => adminStat.id === 'logs')[0];
                const tools = {
                    lowUsageSchools: appStats.lowUsageSchools,
                    highUsageSchools: appStats.highUsageSchools,
                    lowTakeupSchools: appStats.lowTakeupSchools
                };
                delete appStats.lowUsageSchools;
                delete appStats.highUsageSchools;
                delete appStats.lowTakeupSchools;

                const toolsDataSanitized = Object.keys(tools).map((toolKey) => ({ key: toolKey, value: tools[toolKey] }));
                const logsDataSanitized = Object.keys(appLogs).filter((key) => key === 'key' && appLogs[key] === 'logs').map(_ => ({ key: appLogs.key, value: appLogs.value }));
                const appDataSanitized = Object.keys(appStats).filter((key) => appStats[key] !== 'AppStat').map((appStatKey) => ({ key: appStatKey, value: appStats[appStatKey] }));

                setToolsData([...toolsDataSanitized])
                setAdminStatsData([...appDataSanitized, ...logsDataSanitized]);
            }
        }
    });

    const [loadAcademicMonthlyReports, { error: academicReportsError, loading: academicReportsLoading }] = useLazyQuery(ACADEMIC_MONTHLY_REPORTS, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            if (data.academicMonthlyReports) {
                const monthlyReportData = data.academicMonthlyReports;
                setSelectedData(monthlyReportData);
                const monthNumbers = monthlyReportData.map(mr => mr.month);
                labels = [...new Set(monthNumbers.map(month => monthNames[month - 1]))];

                setReadingLogs({
                    labels,
                    datasets: [
                        {
                            label: `Reading Logs ${selectedYear}`,
                            data: computeDataLogs(monthlyReportData, ['totalParentLogs', 'totalStudentLogs', 'totalTeacherLogs'], labels),
                            ...lineColors[0],
                            ...radiusOpts
                        },
                    ],
                });

                setBooksCompleted({
                    labels,
                    datasets: [
                        {
                            label: `Books Completed ${selectedYear}`,
                            data: computeDataLogs(monthlyReportData, ['totalHomeBooks', 'totalLibraryBooks', 'totalSchoolBooks'], labels),
                            ...lineColors[0],
                            ...radiusOpts
                        },
                    ],
                });

                setLoggedBy({
                    labels,
                    datasets: [
                        {
                            label: 'Parent Logs',
                            data: computeDataLogs(monthlyReportData, ['totalParentLogs'], labels),
                            ...lineColors[0],
                            ...radiusOpts
                        },
                        {
                            label: 'Student Logs',
                            data: computeDataLogs(monthlyReportData, ['totalStudentLogs'], labels),
                            ...lineColors[1],
                            ...radiusOpts
                        },
                        {
                            label: 'Teacher Logs',
                            data: computeDataLogs(monthlyReportData, ['totalTeacherLogs'], labels),
                            ...lineColors[2],
                            ...radiusOpts
                        },
                    ],
                });

                setBookTypes({
                    labels,
                    datasets: [
                        {
                            label: 'Home Books',
                            data: computeDataLogs(monthlyReportData, ['totalHomeBooks'], labels),
                            ...lineColors[1],
                            ...radiusOpts
                        },
                        {
                            label: 'Library Books',
                            data: computeDataLogs(monthlyReportData, ['totalLibraryBooks'], labels),
                            ...lineColors[2],
                            ...radiusOpts
                        },
                        {
                            label: 'School Books',
                            data: computeDataLogs(monthlyReportData, ['totalSchoolBooks'], labels),
                            ...lineColors[3],
                            ...radiusOpts
                        },
                    ],
                });

                if (selectedYearType === SELECTED_YEAR_TYPE.CALENDARISTIC) {
                    loadAcademicMonthkyReportsPreviousYear({
                        variables: {
                            calendarYear: selectedYear - 1
                        }
                    });
                } else {
                    loadAcademicMonthkyReportsPreviousYear({
                        variables: {
                            academicYear: selectedYear - 1
                        }
                    });
                }
            }
        }
    });

    const [loadAcademicMonthkyReportsPreviousYear, { data: monthlyReportsDataPreviousYear }] = useLazyQuery(ACADEMIC_MONTHLY_REPORTS, {
        fetchPolicy: 'network-only',
    });

    const computeDataLogs = (graphData, keys, lables) => {
        const dataForLabels = [];
        lables.forEach((label) => {
            const labelIndex = monthNames.indexOf(label);
            const dataForLabelIndex = graphData.filter((item) => item.month - 1 === labelIndex);
            if (dataForLabelIndex) {
                dataForLabels.push(dataForLabelIndex.reduce((acc, curr) => acc + computeKeysSumOfItem(curr, keys), 0))
            }
        });
        return dataForLabels;
    };

    const computeKeysSumOfItem = (monthData, keys) => {
        let sum = 0;
        keys.forEach((key) => {
            sum += monthData[key];
        })
        return sum;
    };

    const handleSelectedYearChange = (date) => {
        setSelectedYear(date.getFullYear());
    };

    const handlesetSelectedYearAdminStats = (date) => {
        const selectedYearAdminStats = date.getFullYear()
        setSelectedYearAdminStats(selectedYearAdminStats);
        getAdminStats({
            variables: selectedYearAdminStats
        })
    }

    const getMaxDate = () => {
        const now = new Date();
        const year = now.getMonth() >= 8 ? now.getFullYear() + 1 : now.getFullYear();
        return new Date(year, 0, 1);
    };

    useEffect(() => {
        if (selectedYearType === SELECTED_YEAR_TYPE.CALENDARISTIC) {
            loadAcademicMonthlyReports({
                variables: {
                    calendarYear: selectedYear
                }
            });
        } else {
            loadAcademicMonthlyReports({
                variables: {
                    academicYear: selectedYear
                }
            });
        }
    }, [selectedYearType, selectedYear]);

    useEffect(() => {
        if (monthlyReportsDataPreviousYear && monthlyReportsDataPreviousYear.academicMonthlyReports) {
            const monthlyReportData = monthlyReportsDataPreviousYear.academicMonthlyReports;
            const monthNumbers = monthlyReportData.map(mr => mr.month);
            labels = [...new Set(monthNumbers.map(month => monthNames[month - 1]))];

            // Reading Logs
            const previoursYeardReadingLogs = {
                label: `Reading Logs ${selectedYear - 1}`,
                data: computeDataLogs(monthlyReportData, ['totalParentLogs', 'totalStudentLogs', 'totalTeacherLogs'], labels),
                ...lineColors[1],
                ...radiusOpts
            };

            // Merge months as chart labels from current / prev year
            const readingLabelsMerged = [...new Set([...readingLogs?.labels, ...labels])];
            // Sort merged months as chart labels based on year type (calendar / academic)
            const readingLabelsSorterd = selectedYearType === SELECTED_YEAR_TYPE.CALENDARISTIC ? readingLabelsMerged.sort((a, b) => calendarMonthOrder[a] - calendarMonthOrder[b]) : readingLabelsMerged.sort(
                (a, b) => academicMonthOrder[a] - academicMonthOrder[b]
            );

            // Since data the dataseet from the prev year is already maped to the corresponding months, we need to adjust that dataset of the current year with respect to the merged labels
            const readingLogsDatasetsAfterMerge = {
                label: readingLogs.datasets[0].label,
                // We basically go through each month and check if the dataset of the current year has data for that month. If not, return 0, otherwise return the data.
                data: readingLabelsSorterd.map((readingLabel) => readingLogs.labels.indexOf(readingLabel) === -1 ? 0 : readingLogs.datasets[0].data[readingLogs.labels.indexOf(readingLabel)]),
                ...lineColors[0],
                ...radiusOpts
            };


            // Books completed
            const previousYearBooksCompleted =
            {
                label: `Books Completed ${selectedYear - 1}`,
                data: computeDataLogs(monthlyReportData, ['totalHomeBooks', 'totalLibraryBooks', 'totalSchoolBooks'], labels),
                ...lineColors[1],
                ...radiusOpts
            };
            // Merge months as chart labels from current / prev year
            const booksCompletedLabelsMerged = [...new Set([...booksCompleted?.labels, ...labels])];
            // Sort merged months as chart labels based on year type (calendar / academic)
            const booksCompletedLabelsSorterd = selectedYearType === SELECTED_YEAR_TYPE.CALENDARISTIC ? booksCompletedLabelsMerged.sort((a, b) => calendarMonthOrder[a] - calendarMonthOrder[b]) : booksCompletedLabelsMerged.sort(
                (a, b) => academicMonthOrder[a] - academicMonthOrder[b]
            );
            // Since data the dataseet from the prev year is already maped to the corresponding months, we need to adjust that dataset of the current year with respect to the merged labels
            const booksCompletedDatasetsAfterMerge = {
                label: booksCompleted.datasets[0].label,
                // We basically go through each month and check if the dataset of the current year has data for that month. If not, return 0, otherwise return the data.
                data: booksCompletedLabelsSorterd.map((bookCompletedlabel) => booksCompleted.labels.indexOf(bookCompletedlabel) === -1 ? 0 : booksCompleted.datasets[0].data[booksCompleted.labels.indexOf(bookCompletedlabel)]),
                ...lineColors[0],
                ...radiusOpts
            };

            // Update data for readingLogs / booksCompleted to contain the data from both current / prev year.
            setReadingLogs({
                labels: readingLabelsSorterd,
                datasets: [previoursYeardReadingLogs, readingLogsDatasetsAfterMerge]
            });
            setBooksCompleted({
                labels: booksCompletedLabelsSorterd,
                datasets: [previousYearBooksCompleted, booksCompletedDatasetsAfterMerge]
            });

            // Update charts
            readingLogsChartRef.current.update();
            booksCompletedChartRef.current.update();

        }

    }, [monthlyReportsDataPreviousYear]);

    useEffectOnce(() => {
        if (!adminStatsData) {
            getAdminStats({
                variables: selectedYearAdminStats
            })
        }
    });

    return (
        <>
            <div className={styles.dataDashboardHeader}>
                <h2>Admin Data Dashboard</h2>
            </div>

            <div className={styles.dataDashboardInsights}>
                <div className={styles.yearSelector}>
                    <h3 className={styles.dataDashboardSubHeadline}>Insights</h3>
                    <DatePicker
                        selected={new Date(selectedYearAdminStats, 0, 1)}
                        showYearPicker
                        dateFormat="yyyy"
                        maxDate={getMaxDate()}
                        onChange={handlesetSelectedYearAdminStats}
                    />
                </div>

                <div className={styles.cardsContainer}>

                    {adminStatsError &&
                        <div style={{ height: '164px' }}>
                            <p>{adminStatsError.message}</p>
                        </div>
                    }

                    {adminStatsLoading &&
                        <div style={{ height: '164px' }}>
                            <p>Loading...</p>
                        </div>
                    }

                    {adminStatsData && adminStatsData.map((stat, index) =>
                        <DashboardCard
                            key={index}
                            cardValue={stat.value}
                            cardDescription={adminStatsMap[stat.key]}
                        />
                    )}
                </div>
            </div>

            <div className={styles.dataDashboardTools}>
                <h3 className={styles.dataDashboardSubHeadline}>Tools</h3>
                <div className={styles.cardsContainer}>

                    {adminStatsError &&
                        <div style={{ height: '82px' }}>
                            <p>{adminStatsError.message}</p>
                        </div>
                    }

                    {adminStatsLoading &&
                        <div style={{ height: '82px' }}>
                            <p>Loading...</p>
                        </div>
                    }

                    {toolsData && toolsData.map((stat, index) =>
                        <div
                            key={index}
                            onClick={() => {
                                navigate(`/dataDashboard/${stat.key}`);
                            }}
                        >
                            <DashboardCard
                                cardValue={stat.value}
                                cardDescription={adminStatsMap[stat.key]}
                                cardValueBackgroundColor={stat.key === 'highUsageSchools' ? '#BAC596' : '#FFA4A4'}
                            />
                        </div>
                    )}

                    <div
                        onClick={() => navigate('/dataDashboard/schoolSearch')}
                    >
                        <DashboardCard
                            cardDescription='School Search'
                            children={<Icon.Search size='20' />}
                        />
                    </div>
                </div>
            </div>

            {academicReportsLoading && <p>Loading...</p>}
            {academicReportsError && <p>{academicReportsError.message}</p>}

            <div className={styles.yearSelector}>
                <Tabs>
                    <Tab
                        selected={selectedYearType === SELECTED_YEAR_TYPE.CALENDARISTIC}
                        onClick={() => {
                            setSelectedYearType(SELECTED_YEAR_TYPE.CALENDARISTIC)
                            setSelectedYear(moment().year())
                        }}
                    >
                        Calendar
                    </Tab>
                    <Tab
                        selected={selectedYearType === SELECTED_YEAR_TYPE.ACADEMIC}
                        onClick={() => {
                            setSelectedYearType(SELECTED_YEAR_TYPE.ACADEMIC);
                            setSelectedYear(moment().toDate().getMonth() >= 8 ? moment().year() + 1 : moment().year())
                        }}
                    >
                        Academic
                    </Tab>
                </Tabs>

                <DatePicker
                    selected={new Date(selectedYear, 0, 1)}
                    showYearPicker
                    dateFormat="yyyy"
                    maxDate={moment().add(1, 'year').toDate()}
                    onChange={handleSelectedYearChange}
                />
            </div>

            {selectedData && <div className={styles.dataDashboardGraphs}>
                {readingLogs && (<div>
                    <div className={styles.ProgressWrapper}>
                        <span className={styles.Title}>Reading logs</span>
                        <div className={styles.Timeline}>
                            <div className={styles.Progress}></div>
                        </div>
                    </div>

                    <Line

                        ref={readingLogsChartRef}
                        options={options}
                        data={readingLogs}
                    />
                </div>)}

                {booksCompleted && (<div>
                    <div className={styles.ProgressWrapper}>
                        <span className={styles.Title}>Books Completed</span>
                        <div className={styles.Timeline}>
                            <div className={styles.Progress}></div>
                        </div>
                    </div>

                    <Line
                        ref={booksCompletedChartRef}
                        options={options}
                        data={booksCompleted}
                    />
                </div>)}

                {bookTypes && (<div>
                    <div className={styles.ProgressWrapper}>
                        <span className={styles.Title}>Book Types</span>
                        <div className={styles.Timeline}>
                            <div className={styles.Progress}></div>
                        </div>
                    </div>

                    <Line
                        options={options}
                        data={bookTypes}
                    />
                </div>)}

                {loggedBy && (<div>
                    <div className={styles.ProgressWrapper}>
                        <span className={styles.Title}>Logged By</span>
                        <div className={styles.Timeline}>
                            <div className={styles.Progress}></div>
                        </div>
                    </div>
                    <Line
                        options={options}
                        data={loggedBy}
                    />
                </div>)}
            </div>}
        </>
    );
};

export default DataDashboard;
